import React, { useState } from 'react';
import {
  AudioEffectsState,
  DistortionParams,
} from '@/hooks/useAudioEffects';

interface AudioEffectsPanelProps {
  state: AudioEffectsState;
  isProcessing: boolean;
  error: string | null;
  onUpdateEffect: <K extends keyof AudioEffectsState>(
    effect: K,
    params: Partial<AudioEffectsState[K]>
  ) => void;
  onToggleEffect: (effect: keyof Omit<AudioEffectsState, 'masterGain'>) => void;
  onResetEffects: () => void;
  onPlayWithEffects: () => void;
  onProcessAudio: () => Promise<void>;
  onStopPlayback: () => void;
  hasRecording: boolean;
  isPlaying: boolean;
}

const FILTER_TYPES: { value: BiquadFilterType; label: string }[] = [
  { value: 'lowpass', label: 'Low Pass' },
  { value: 'highpass', label: 'High Pass' },
  { value: 'bandpass', label: 'Band Pass' },
  { value: 'notch', label: 'Notch' },
  { value: 'peaking', label: 'Peaking' },
  { value: 'lowshelf', label: 'Low Shelf' },
  { value: 'highshelf', label: 'High Shelf' },
];

const DISTORTION_TYPES = [
  { value: 'soft', label: 'Soft Clip' },
  { value: 'hard', label: 'Hard Clip' },
  { value: 'fuzz', label: 'Fuzz' },
  { value: 'bitcrush', label: 'Bitcrush' },
];

// Slider component
const Slider: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
  disabled?: boolean;
}> = ({ label, value, min, max, step = 1, unit = '', onChange, disabled }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
      <span>{label}</span>
      <span className="font-mono">{value.toFixed(step < 1 ? 2 : 0)}{unit}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      disabled={disabled}
      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
    />
  </div>
);

// Effect card wrapper
const EffectCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  enabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  color: string;
}> = ({ title, icon, enabled, onToggle, children, color }) => (
  <div className={`rounded-xl border-2 transition-all duration-200 ${
    enabled
      ? `border-${color}-500 bg-${color}-50 dark:bg-${color}-900/20`
      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
  }`}>
    <button
      onClick={onToggle}
      className={`w-full flex items-center justify-between p-3 rounded-t-xl transition-colors ${
        enabled
          ? `bg-${color}-100 dark:bg-${color}-900/30`
          : 'bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={enabled ? `text-${color}-600 dark:text-${color}-400` : 'text-gray-500 dark:text-gray-400'}>
          {icon}
        </span>
        <span className={`font-medium ${enabled ? `text-${color}-700 dark:text-${color}-300` : 'text-gray-700 dark:text-gray-300'}`}>
          {title}
        </span>
      </div>
      <div className={`w-10 h-6 rounded-full p-1 transition-colors ${
        enabled ? `bg-${color}-500` : 'bg-gray-300 dark:bg-gray-600'
      }`}>
        <div className={`w-4 h-4 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-4' : 'translate-x-0'
        }`} />
      </div>
    </button>
    <div className={`p-4 space-y-3 ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
      {children}
    </div>
  </div>
);

const AudioEffectsPanel: React.FC<AudioEffectsPanelProps> = ({
  state,
  isProcessing,
  error,
  onUpdateEffect,
  onToggleEffect,
  onResetEffects,
  onPlayWithEffects,
  onProcessAudio,
  onStopPlayback,
  hasRecording,
  isPlaying,
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'custom'>('basic');

  const tabs = [
    { id: 'basic', label: 'Basic', icon: '🎚️' },
    { id: 'advanced', label: 'Advanced', icon: '🎛️' },
    { id: 'custom', label: 'Custom DSP', icon: '💻' },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Audio Effects</h3>
              <p className="text-xs text-white/70">Web Audio API powered effects</p>
            </div>
          </div>
          <button
            onClick={onResetEffects}
            className="px-3 py-1.5 text-sm bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors"
          >
            Reset All
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
        {/* Master Gain */}
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <Slider
            label="🔊 Master Volume"
            value={state.masterGain}
            min={0}
            max={2}
            step={0.1}
            onChange={(v) => onUpdateEffect('masterGain' as never, v as never)}
          />
        </div>

        {/* Basic Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            {/* Pitch Shift */}
            <EffectCard
              title="Pitch Shift"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>}
              enabled={state.pitchShift.enabled}
              onToggle={() => onToggleEffect('pitchShift')}
              color="blue"
            >
              <Slider
                label="Semitones"
                value={state.pitchShift.semitones}
                min={-12}
                max={12}
                step={1}
                unit=" st"
                onChange={(v) => onUpdateEffect('pitchShift', { semitones: v })}
                disabled={!state.pitchShift.enabled}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>-1 Octave</span>
                <span>Original</span>
                <span>+1 Octave</span>
              </div>
            </EffectCard>

            {/* 3-Band EQ */}
            <EffectCard
              title="3-Band EQ"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>}
              enabled={state.eq.enabled}
              onToggle={() => onToggleEffect('eq')}
              color="green"
            >
              <Slider
                label="Low (Bass)"
                value={state.eq.lowGain}
                min={-12}
                max={12}
                step={0.5}
                unit=" dB"
                onChange={(v) => onUpdateEffect('eq', { lowGain: v })}
                disabled={!state.eq.enabled}
              />
              <Slider
                label="Mid"
                value={state.eq.midGain}
                min={-12}
                max={12}
                step={0.5}
                unit=" dB"
                onChange={(v) => onUpdateEffect('eq', { midGain: v })}
                disabled={!state.eq.enabled}
              />
              <Slider
                label="High (Treble)"
                value={state.eq.highGain}
                min={-12}
                max={12}
                step={0.5}
                unit=" dB"
                onChange={(v) => onUpdateEffect('eq', { highGain: v })}
                disabled={!state.eq.enabled}
              />
            </EffectCard>

            {/* Reverb */}
            <EffectCard
              title="Reverb"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4" />
              </svg>}
              enabled={state.reverb.enabled}
              onToggle={() => onToggleEffect('reverb')}
              color="purple"
            >
              <Slider
                label="Mix (Dry/Wet)"
                value={state.reverb.wet}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => onUpdateEffect('reverb', { wet: v })}
                disabled={!state.reverb.enabled}
              />
              <Slider
                label="Decay Time"
                value={state.reverb.decay}
                min={0.1}
                max={10}
                step={0.1}
                unit=" s"
                onChange={(v) => onUpdateEffect('reverb', { decay: v })}
                disabled={!state.reverb.enabled}
              />
              <Slider
                label="Pre-Delay"
                value={state.reverb.preDelay}
                min={0}
                max={100}
                step={5}
                unit=" ms"
                onChange={(v) => onUpdateEffect('reverb', { preDelay: v })}
                disabled={!state.reverb.enabled}
              />
            </EffectCard>
          </div>
        )}

        {/* Advanced Tab */}
        {activeTab === 'advanced' && (
          <div className="space-y-4">
            {/* Filter */}
            <EffectCard
              title="Filter"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>}
              enabled={state.filter.enabled}
              onToggle={() => onToggleEffect('filter')}
              color="cyan"
            >
              <div className="space-y-2">
                <label className="text-xs text-gray-600 dark:text-gray-400">Filter Type</label>
                <select
                  value={state.filter.type}
                  onChange={(e) => onUpdateEffect('filter', { type: e.target.value as BiquadFilterType })}
                  disabled={!state.filter.enabled}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 disabled:opacity-50"
                >
                  {FILTER_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <Slider
                label="Frequency"
                value={state.filter.frequency}
                min={20}
                max={20000}
                step={10}
                unit=" Hz"
                onChange={(v) => onUpdateEffect('filter', { frequency: v })}
                disabled={!state.filter.enabled}
              />
              <Slider
                label="Q (Resonance)"
                value={state.filter.Q}
                min={0.1}
                max={20}
                step={0.1}
                onChange={(v) => onUpdateEffect('filter', { Q: v })}
                disabled={!state.filter.enabled}
              />
              {['lowshelf', 'highshelf', 'peaking'].includes(state.filter.type) && (
                <Slider
                  label="Gain"
                  value={state.filter.gain}
                  min={-40}
                  max={40}
                  step={1}
                  unit=" dB"
                  onChange={(v) => onUpdateEffect('filter', { gain: v })}
                  disabled={!state.filter.enabled}
                />
              )}
            </EffectCard>

            {/* Distortion */}
            <EffectCard
              title="Distortion"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>}
              enabled={state.distortion.enabled}
              onToggle={() => onToggleEffect('distortion')}
              color="red"
            >
              <div className="space-y-2">
                <label className="text-xs text-gray-600 dark:text-gray-400">Distortion Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {DISTORTION_TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => onUpdateEffect('distortion', { type: t.value as DistortionParams['type'] })}
                      disabled={!state.distortion.enabled}
                      className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                        state.distortion.type === t.value
                          ? 'bg-red-500 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      } disabled:opacity-50`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <Slider
                label="Amount"
                value={state.distortion.amount}
                min={0}
                max={100}
                step={1}
                unit="%"
                onChange={(v) => onUpdateEffect('distortion', { amount: v })}
                disabled={!state.distortion.enabled}
              />
            </EffectCard>

            {/* Delay/Echo */}
            <EffectCard
              title="Delay / Echo"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>}
              enabled={state.delay.enabled}
              onToggle={() => onToggleEffect('delay')}
              color="amber"
            >
              <Slider
                label="Delay Time"
                value={state.delay.time}
                min={0}
                max={2000}
                step={10}
                unit=" ms"
                onChange={(v) => onUpdateEffect('delay', { time: v })}
                disabled={!state.delay.enabled}
              />
              <Slider
                label="Feedback"
                value={state.delay.feedback}
                min={0}
                max={0.95}
                step={0.05}
                onChange={(v) => onUpdateEffect('delay', { feedback: v })}
                disabled={!state.delay.enabled}
              />
              <Slider
                label="Mix (Dry/Wet)"
                value={state.delay.wet}
                min={0}
                max={1}
                step={0.05}
                onChange={(v) => onUpdateEffect('delay', { wet: v })}
                disabled={!state.delay.enabled}
              />
            </EffectCard>
          </div>
        )}

        {/* Custom DSP Tab */}
        {activeTab === 'custom' && (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div className="text-sm text-amber-700 dark:text-amber-300">
                  <p className="font-medium">Experimental Feature</p>
                  <p className="text-xs mt-1">Custom DSP code runs client-side. Invalid code may cause errors or audio glitches.</p>
                </div>
              </div>
            </div>

            <EffectCard
              title="Custom DSP"
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>}
              enabled={state.customDSP.enabled}
              onToggle={() => onToggleEffect('customDSP')}
              color="indigo"
            >
              <div className="space-y-2">
                <label className="text-xs text-gray-600 dark:text-gray-400">JavaScript DSP Code</label>
                <textarea
                  value={state.customDSP.code}
                  onChange={(e) => onUpdateEffect('customDSP', { code: e.target.value })}
                  disabled={!state.customDSP.enabled}
                  className="w-full h-48 px-3 py-2 font-mono text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-900 text-green-400 disabled:opacity-50 resize-none"
                  placeholder="// Write your custom DSP code here..."
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <p><strong>Available:</strong> samples (Float32Array), sampleRate (number)</p>
                <p><strong>Modify samples in place to apply effect</strong></p>
              </div>
            </EffectCard>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div className="flex flex-wrap gap-3 justify-center">
          <button
            onClick={isPlaying ? onStopPlayback : onPlayWithEffects}
            disabled={!hasRecording || isProcessing}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
              !hasRecording || isProcessing
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                : isPlaying
                ? 'bg-orange-500 hover:bg-orange-600 text-white'
                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
            }`}
          >
            {isPlaying ? (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
                Stop Preview
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Preview Effects
              </>
            )}
          </button>

          <button
            onClick={onProcessAudio}
            disabled={!hasRecording || isProcessing}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
              !hasRecording || isProcessing
                ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {isProcessing ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Apply & Export
              </>
            )}
          </button>
        </div>

        {!hasRecording && (
          <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
            Record audio first to apply effects
          </p>
        )}
      </div>
    </div>
  );
};

export default AudioEffectsPanel;
